import { formatDistance } from 'date-fns';
import { useGT } from 'gt-react';
import type {
  DataSourceLoadOverview,
  DirectLoadBatchHistoryEntry,
  LinkLoad,
} from '@erp/rpc';

/** Badge variant per load status. */
export const LOAD_STATUS_VARIANT: Record<
  LinkLoad['status'],
  'secondary' | 'success' | 'warning' | 'destructive'
> = {
  running: 'secondary',
  succeeded: 'success',
  partial: 'warning',
  failed: 'destructive',
  cancelled: 'secondary',
};

/** Translated label per load status. */
export function useLoadStatusLabels(): Record<LinkLoad['status'], string> {
  const gt = useGT();
  return {
    running: gt('Running', { context: 'direct load status' }),
    succeeded: gt('Succeeded', { context: 'direct load status' }),
    partial: gt('Partial', { context: 'direct load status' }),
    failed: gt('Failed', { context: 'direct load status' }),
    cancelled: gt('Cancelled', { context: 'direct load status' }),
  };
}

/**
 * Milliseconds from the load's (or batch's) start to its finish, or to `now`
 * while it runs; null before its first chunk started.
 */
export function elapsedMs(
  load: Pick<LinkLoad, 'startedAt' | 'finishedAt'>,
  now: number,
): number | null {
  if (!load.startedAt) return null;
  const end = load.finishedAt ? Date.parse(load.finishedAt) : now;
  return end - Date.parse(load.startedAt);
}

/** Rows read per second over the load's elapsed time. */
export function throughput(load: LinkLoad, now: number): number | null {
  const ms = elapsedMs(load, now);
  return ms !== null && ms > 0 ? load.rowsRead / (ms / 1000) : null;
}

export const count = (n: number) => n.toLocaleString();

type BatchHistoryStatus = DirectLoadBatchHistoryEntry['status'];
type BatchLinkOutcome = DirectLoadBatchHistoryEntry['links'][number]['status'];

/** Badge variant per batch status, as the batch history shows it. */
export const BATCH_HISTORY_STATUS_VARIANT: Record<
  BatchHistoryStatus,
  'outline' | 'secondary' | 'success' | 'warning' | 'destructive'
> = {
  ...LOAD_STATUS_VARIANT,
  incomplete: 'warning',
  skipped: 'outline',
};

/** Translated label per batch status. */
export function useBatchHistoryStatusLabels(): Record<
  BatchHistoryStatus,
  string
> {
  const gt = useGT();
  return {
    ...useLoadStatusLabels(),
    incomplete: gt('Incomplete', {
      context: 'batch status: stopped before reaching every link',
    }),
    skipped: gt('Skipped', {
      context: 'batch status: never started because a load was running',
    }),
  };
}

/** Badge variant per planned link's outcome in a batch. */
export const BATCH_LINK_OUTCOME_VARIANT: Record<
  BatchLinkOutcome,
  'outline' | 'secondary' | 'success' | 'warning' | 'destructive'
> = {
  ...LOAD_STATUS_VARIANT,
  pending: 'outline',
  not_started: 'outline',
  empty: 'secondary',
};

/** Translated label per planned link's outcome in a batch. */
export function useBatchLinkOutcomeLabels(): Record<BatchLinkOutcome, string> {
  const gt = useGT();
  return {
    ...useLoadStatusLabels(),
    pending: gt('Pending', { context: 'batch link status: not reached yet' }),
    not_started: gt('Not started', {
      context: 'batch link status: the batch stopped before reaching it',
    }),
    empty: gt('Empty file', {
      context: 'batch link status: its file had no rows to load',
    }),
  };
}

/** Fill colour per outcome, for the batch's outcome bar and legend dots. */
export const BATCH_LINK_OUTCOME_COLOR: Record<BatchLinkOutcome, string> = {
  succeeded: 'bg-emerald-500',
  empty: 'bg-emerald-300 dark:bg-emerald-700',
  partial: 'bg-amber-500',
  failed: 'bg-red-500',
  cancelled: 'bg-zinc-500',
  not_started: 'bg-zinc-300 dark:bg-zinc-600',
  running: 'bg-sky-500',
  pending: 'bg-sky-200 dark:bg-sky-900',
};

/** Outcomes in the order the bar and the counts list them. */
export const BATCH_LINK_OUTCOME_ORDER: readonly BatchLinkOutcome[] = [
  'succeeded',
  'empty',
  'partial',
  'failed',
  'cancelled',
  'not_started',
  'running',
  'pending',
];

/**
 * A batch's end-to-end time: counting to `now` while it runs, its recorded
 * duration once over (null when it stopped with no finish to time it by).
 */
export function batchElapsedMs(
  batch: Pick<
    DirectLoadBatchHistoryEntry,
    'status' | 'startedAt' | 'finishedAt' | 'durationMs'
  >,
  now: number,
): number | null {
  return batch.status === 'running' ? elapsedMs(batch, now) : batch.durationMs;
}

/** How many links of a batch ended each way, in the bar's order, zeros dropped. */
export function outcomeCounts(
  links: readonly DirectLoadBatchHistoryEntry['links'][number][],
): { outcome: BatchLinkOutcome; count: number }[] {
  return BATCH_LINK_OUTCOME_ORDER.flatMap((outcome) => {
    const n = links.filter((link) => link.status === outcome).length;
    return n > 0 ? [{ outcome, count: n }] : [];
  });
}

/** "3 hours ago", measured to `now` (the fetch time) rather than the clock. */
export function since(iso: string, now: number): string {
  return formatDistance(new Date(iso), new Date(now), { addSuffix: true });
}

/** The latest thing that happened to a data source: a finished load or a batch request. */
function lastActivity(source: DataSourceLoadOverview): number {
  return Math.max(
    source.lastLoadedAt ? Date.parse(source.lastLoadedAt) : 0,
    source.lastBatch ? Date.parse(source.lastBatch.requestedAt) : 0,
  );
}

/**
 * The rows worth a line: data sources with a loadable link or any history,
 * running first, then most recently active, then never loaded in the
 * server's (group, title) order. The rest only have links a direct load
 * refuses, and are counted instead.
 */
export function orderOverviewRows(dataSources: DataSourceLoadOverview[]): {
  rows: DataSourceLoadOverview[];
  hidden: number;
} {
  const shown = dataSources.filter(
    (source) =>
      source.links.total > source.links.notLoadable ||
      source.lastLoadedAt !== null ||
      source.lastBatch !== null,
  );
  const rows = shown
    .map((source, index) => ({ source, index }))
    .sort(
      (a, b) =>
        Number(b.source.running) - Number(a.source.running) ||
        lastActivity(b.source) - lastActivity(a.source) ||
        a.index - b.index,
    )
    .map(({ source }) => source);
  return { rows, hidden: dataSources.length - shown.length };
}
